import { v4 as uuid } from 'uuid';
import {
  Box, Grid, Link,
} from '@mui/material';
import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import styles from './BlockCardCentered.module.css';
import BlockCardCenteredData, { BlockCardCenteredVariant } from '../../../models/Block/BlockCardCenteredData';
import useResponsive from '../../../hooks/UseResponsive';
import CroppedImage from '../../CroppedImage';
import ImageSettingsModal from '../../Modals/ImageSettingsModal/ImageSettingsModal';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import useGetBackground from '../../../hooks/UseGetBackground';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';
import ImageEnlargeModal from '../../Images/ImageEnlargeModal';

export interface BlockCardCenteredProps extends BlockProps {
  blockData: BlockData[],
  isFullBleed?: boolean,
}

function BlockCardCentered({
  data,
  isEditing,
  isTemplate,
  blockData,
  isFullBleed = false,
  editHandler,
  showImageModal,
  onCloseImageModal,
  mainColor,
}: BlockCardCenteredProps) {
  const targetModalId = uuid();

  const [props, setProps] = useState<BlockCardCenteredData>(data.data as BlockCardCenteredData);

  const onUpdate = useCallback((newImage: BlockComponentImage) => {
    props.mainImage = newImage;
    editHandler(data.id, props);
  }, [props, data.id, editHandler]);
  const cardItemId = uuid();

  const deleteImage = useCallback(() => {
    props.mainImage = undefined;
    editHandler(data.id, props);
  }, [data.id, editHandler, props]);

  useEffect(() => {
    setProps(data.data as BlockCardCenteredData);
  }, [data.data]);

  useResponsive({
    items: [
      props.variant === BlockCardCenteredVariant.Left
        ? {
          wrapperId: `card-centered-wrapper-${cardItemId}`,
          targetId: `card-centered-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideLeftSmartphonePreview },
            { size: 600, className: isFullBleed ? styles.textSideLeftBleed : styles.textSideLeft },
          ],
        } : {
          wrapperId: `card-centered-wrapper-${cardItemId}`,
          targetId: `card-centered-text-${cardItemId}`,
          queries: [
            { size: 0, className: styles.textSideRightSmartphonePreview },
            { size: 600, className: isFullBleed ? styles.textSideRightBleed : styles.textSideRight },
          ],
        },

      props.variant === BlockCardCenteredVariant.Right && {
        wrapperId: `card-centered-wrapper-${cardItemId}`,
        targetId: `card-centered-wrapper-${cardItemId}`,
        queries: [
          { size: 0, className: styles.containerSmartphonePreview },
          { size: 600, className: styles.container },
        ],
      },
    ],
  });

  const imageSide = useMemo(() => (
    <Grid
      item
      xs={6}
      minWidth={300}
    >

      {props.mainImage && (
        <Box sx={Utils.hoverBorder(isEditing)}>

          {isEditing && <CloseButton updateHandler={() => { deleteImage(); }} />}
          <ImageSettingsModal isEditing={isEditing} image={props.mainImage} disabled={isTemplate || !isEditing} blockData={blockData} onConfirm={onUpdate}>
            {(!isEditing) ? (
              <Link
                style={{ cursor: props?.mainImage?.link?.linkData?.url ? 'pointer' : 'default' }}
                target={props?.mainImage?.link?.linkData?.openType}
                href={props?.mainImage?.link?.linkData?.url || undefined}
                sx={{
                  width: '100%',
                  height: '100%',
                }}
                rel="noreferrer"
              >
                <CroppedImage
                  isEditing={isEditing}
                  image={props.mainImage}
                  targetModalId={targetModalId}
                  settings={props.mainImage.settings}
                />
              </Link>
            ) : (
              <CroppedImage
                isEditing={isEditing}
                image={props.mainImage}
                targetModalId={targetModalId}
                settings={props.mainImage.settings}
              />
            )}
          </ImageSettingsModal>
        </Box>
      )}

    </Grid>
  ), [props, isEditing, isTemplate, deleteImage, blockData, onUpdate]);

  const textSide = useMemo(() => (
    <Grid
      item
      id={`card-centered-text-${cardItemId}`}
      xs={6}
      minWidth={300}
      alignItems="start"
      justifyContent="start"
    >
      {props.title && (
        <Grid
          item
          xs="auto"
          alignContent="start"
          justifyContent="start"
          sx={{
            paddingTop: 2,
            marginBottom: 2,
          }}
        >
          <Box sx={Utils.hoverBorder(isEditing)}>

            {isEditing && (
              <CloseButton updateHandler={() => {
                props.title = undefined;
                editHandler(data.id, props);
              }}
              />
            )}

            <EditableText
              text={props.title.value}
              textColor={props.title.settings?.textColor}
              isEditable={isEditing && !isTemplate}
              updateHandler={(text) => {
                props.title.value = text;
                editHandler(data.id, props);
              }}
            />
          </Box>
        </Grid>
      )}

      {props.description && (
        <Grid
          item
          xs="auto"
          sx={[Utils.hoverBorder(isEditing), { marginBottom: 2 }]}
        >

          {isEditing && (
            <CloseButton updateHandler={() => {
              props.description = undefined;
              editHandler(data.id, props);
            }}
            />
          )}

          <EditableText
            text={props.description.value}
            textColor={props.description.settings?.textColor}
            isEditable={isEditing && !isTemplate}
            updateHandler={(text) => {
              props.description.value = text;
              editHandler(data.id, props);
            }}
          />

        </Grid>
      )}

      {props.button && (
        <Box sx={Utils.hoverBorder(isEditing)}>
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.button = undefined;
              editHandler(data.id, props);
            }}
          />
          <LinkButton
            mainColor={mainColor}
            button={props.button}
            blockData={blockData}
            isEditable={isEditing && !isTemplate}
            isTemplate={isTemplate}
            updateHandler={(item) => {
              props.button = item;
              editHandler(data.id, props);
            }}
          />
        </Box>
      )}
    </Grid>
  ), [cardItemId, props, isEditing, isTemplate, blockData, editHandler, data.id]);

  const elementUuid = uuid();
  useParallaxScript(props.backgroundImage?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <>
      <Grid
        id={elementUuid}
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: '250px',
          overflow: 'hidden',
          ...useGetBackground(
            props.backgroundImage?.solidColor,
            props.backgroundImage?.value,
            props.backgroundImage?.settings?.scale,
            props.backgroundImage?.settings?.scaleBool,
          ),
          padding: !isFullBleed || isEditing || isTemplate ? 2 : 0,
          backgroundPosition: 'center 0px',
        }}
      >
        <Grid
          container
          spacing={0}
          direction="column"
          justifyContent="end"
        >

          {(isEditing && !isTemplate) && (
            <BackGroundImageModal
              backgroundImage={props.backgroundImage}
              isShowModal={showImageModal}
              onCloseModal={onCloseImageModal}
              handleOnConfirm={(url: BlockComponentImage) => {
                const tempBg = { ...props, backgroundImage: { ...url } };
                setProps(tempBg);
                editHandler(data.id, tempBg);
              }}
            />
          )}
        </Grid>

        <Grid
          id={`card-centered-wrapper-${cardItemId}`}
          container
          spacing={0}
          direction="row"
          alignItems="center"
          justifyContent="center"
          className="block-content-wrapper"
        >
          {props.variant === BlockCardCenteredVariant.Left ? (
            <>
              {imageSide}
              {textSide}
            </>
          ) : (
            <>
              {textSide}
              {imageSide}
            </>
          )}
        </Grid>

      </Grid>

      <ImageEnlargeModal
        images={[props?.mainImage]}
        targetModalId={targetModalId}
      />
    </>
  );
}

export default BlockCardCentered;
